import pathNode from 'path';

import {
  LARGE_BREAKPOINT,
  MEDIUM_BREAKPOINT,
  SMALL_BREAKPOINT,
} from '@/components/modular-grid/constants';
import { FunctionalitiesEnum } from '@/config/types';
import { getSpecialRoutePath } from '@/logic/main/utils';
import {
  BAD_GAME,
  DYNAMIC_PATH_REGEX,
  IMAGE_SIZES,
  PORTAL_VERTICAL_GAME_TYPES,
} from '@/model/algolia/constants';
import { GameFromAlgolia, TileAnimationData } from '@/model/algolia/types';
import {
  GameTableType,
  PortalVerticalGameTilePropTypes,
  PortalVerticalTableGameType,
  Table,
} from '@/types/gameTypes';

import { createLaunchUrl } from './launchUrl';

export const mapPlayersInfo = ({
  seats,
  players = 0,
}: {
  seats?: number;
  players?: number;
}) => (seats ? `${players} / ${seats}` : `${players}`);

export function convertToObject<T>(result: T[], keys: string[]) {
  return keys.reduce(
    (acc: object, item: string, index: number) => ({
      ...acc,
      [item]: result[index],
    }),
    {}
  );
}

export function filterCloseLiveTableGames(
  games: PortalVerticalGameTilePropTypes[] = []
) {
  return games.filter(
    game => !(game.table_id && game.table?.online.open === false)
  );
}

export const prepareImages = (
  gameImage: PortalVerticalGameTilePropTypes['image']
) => {
  const smallTile = IMAGE_SIZES.small.map(config => ({
    min: config.min,
    max: config.max,
    src: transformImage(gameImage.source.base, config.size),
  }));

  const largeTile = IMAGE_SIZES.large.map(config => ({
    min: config.min,
    max: config.max,
    src: transformImage(gameImage.source.base, config.size),
  }));

  return {
    smallTile,
    largeTile,
  };
};

export const mapTableType = (table?: Table): GameTableType | undefined =>
  table && table.type ? PORTAL_VERTICAL_GAME_TYPES[table.type] : undefined;

export const mapTableStake = (table?: Table): string => {
  const stake = table && table.stake;

  return stake
    ? `${stake.text || 'Stake'} ${stake.currencySymbol}${stake.min}`
    : '';
};

export const mapTable = (
  table?: Table
): PortalVerticalTableGameType | undefined => {
  return (
    table && {
      ...table,
      type: mapTableType(table),
      playersInfo: mapPlayersInfo(table),
      stake: mapTableStake(table),
    }
  );
};

const isGameInvalid = (game: GameFromAlgolia) =>
  !game || game.tags.some(tag => BAD_GAME.indexOf(tag) >= 0);

export const filterInvalidGames = (games: GameFromAlgolia[]) =>
  games.filter((game: GameFromAlgolia) => !isGameInvalid(game));

export const reduceArrayToObj = (array: { id: string }[]) =>
  array.reduce((acc, elem) => ({ ...acc, [elem.id]: elem }), {});

const getImgObj = (image: string, width: number, webpFormat?: boolean) => ({
  normal: `${image}?quality=65&width=${width}${
    webpFormat ? `&format=webply` : ''
  }`,
  retina: `${image}?quality=65&width=${width * 1.5}${
    webpFormat ? `&format=webply` : ''
  }`,
});

export const transformImage = (image: string = '', width: number = 200) => {
  const supportedFormats = /.jpg|.png/i;
  const isWebpAvailable = !!image.match(supportedFormats);

  return isWebpAvailable
    ? {
        ...getImgObj(image, width),
        modern: {
          ...getImgObj(image, width, isWebpAvailable),
        },
      }
    : getImgObj(image, width);
};

export function appendTableToGame(
  game: PortalVerticalGameTilePropTypes,
  table?: Table
) {
  return {
    ...game,
    table: table || undefined,
    image: game.image || table?.dealer?.thumbnail || '',
    tableFetching: false,
  };
}

export function mapImage(
  gameImage?: { url: string; title: string; description?: string },
  table?: Table,
  width?: number
) {
  const image = gameImage?.url || table?.dealer?.thumbnail;
  const alt = gameImage?.description || gameImage?.title || '';
  return image
    ? { alt, source: transformImage(image, width) }
    : { source: { normal: '' }, alt };
}

function mapTileAnimationData(animation: TileAnimationData) {
  return {
    title: animation.title,
    animationType: animation.animationType,
    interval: animation.interval,
    asset: mapImage(animation.asset),
  };
}

function mapBannerImage(bannerImage?: {
  url: string;
  title: string;
  description?: string;
  content_type: string;
}) {
  if (!bannerImage) return undefined;
  const image = bannerImage.url;
  const alt = bannerImage.description || bannerImage.title || '';

  return {
    alt,
    type: bannerImage.content_type.split('/')[0] as 'image',
    source: {
      normal: image,
    },
  };
}

function mapGameInfoImages(info?: GameFromAlgolia['info_page']) {
  if (
    !info ||
    !info.image_set[0] ||
    !info.image_set[0].large_background ||
    !info.image_set[0].small_medium_background
  )
    return undefined;

  return {
    logo: {
      source: transformImage(info.image_set[0].logo.url, 300),
      alt: info.image_set[0].logo.description || info.image_set[0].logo.title,
    },
    background: [
      {
        max: SMALL_BREAKPOINT.max,
        src: transformImage(
          info.image_set[0].small_medium_background.url,
          SMALL_BREAKPOINT.max - 50
        ),
      },
      {
        min: MEDIUM_BREAKPOINT.min,
        max: MEDIUM_BREAKPOINT.max,
        src: transformImage(info.image_set[0].small_medium_background.url, 375),
      },
      {
        min: LARGE_BREAKPOINT.min,
        src: transformImage(info.image_set[0].large_background.url, 480),
      },
    ],
  };
}

function mapGameImagesToBeSized(
  gameImage?: { url: string; title: string; description?: string },
  table?: Table
) {
  const image = gameImage?.url || table?.dealer?.thumbnail;
  return image
    ? {
        smallTile: [
          {
            max: 500,
            src: transformImage(image, 90),
          },
          {
            max: SMALL_BREAKPOINT.max,
            src: transformImage(image, 160),
          },
          {
            min: MEDIUM_BREAKPOINT.min,
            max: MEDIUM_BREAKPOINT.max,
            src: transformImage(image, 110),
          },
          {
            min: LARGE_BREAKPOINT.min,
            src: transformImage(image, 160),
          },
        ],
        largeTile: [
          {
            max: 500,
            src: transformImage(image, 140),
          },
          {
            max: SMALL_BREAKPOINT.max,
            src: transformImage(image, 270),
          },
          {
            min: MEDIUM_BREAKPOINT.min,
            max: MEDIUM_BREAKPOINT.max,
            src: transformImage(image, 280),
          },
          {
            min: LARGE_BREAKPOINT.min,
            src: transformImage(image, 330),
          },
        ],
      }
    : undefined;
}

function getGameDetailsPath(game: GameFromAlgolia) {
  const baseGameDetailsPath =
    game.gameDetailsPath && DYNAMIC_PATH_REGEX.test(game.gameDetailsPath)
      ? game.gameDetailsPath.replace(DYNAMIC_PATH_REGEX, '')
      : getSpecialRoutePath(FunctionalitiesEnum.gameDetails);

  return `${baseGameDetailsPath}/${game.presentation_code}`;
}

/*
  posix because we need to support both windows and unix
  otherwise the output will be different
  
  windows: \\games\\code
  unix: /games/code
*/
function createDynamicPath(basePath: string, path: string) {
  return pathNode.posix.join(basePath, path);
}

export function mapAlgoliaGame({
  game,
  table,
  locale,
  base_path = '',
  game_launch_placement,
}: {
  game: GameFromAlgolia;
  table?: Table;
  locale?: string;
  base_path?: string;
  game_launch_placement?: string;
}): PortalVerticalGameTilePropTypes {
  const jackpotVelocity = Number(game.jackpot_velocity);

  const mappedGame = {
    id: game.uid,
    table_id: game.table_id,
    origin: game.origin,
    sizedImages: mapGameImagesToBeSized(game.image, table),
    sizedImagesLoggedIn: mapGameImagesToBeSized(game.image_logged_in, table),
    presentation_code: game.presentation_code,
    transaction_code: game.transaction_code,
    transactional_game_code: game.transactional_game_code,
    name: game.title,
    title: game.title.replace(/ - duplicate \d$/, ''),
    info_title: game.info_title,
    type: game.type_global?.game_type,
    description: game.description,
    provider: game.provider,
    integration: game.launch_configuration.integration,
    features: game.features,
    jackpotCode: game.jackpot_code,
    ...(!isNaN(jackpotVelocity) && { jackpotVelocity }),
    // TODO -> consider default image in case when game has image set to null
    image: mapImage(game.image, table),
    image_logged_in: mapImage(game.image_logged_in, table),
    landscape_image: mapImage(game.landscape_image, undefined, 250),
    landscape_image_logged_in: mapImage(
      game.landscape_image_logged_in,
      undefined,
      250
    ),
    ...(game?.logo && {
      logo: mapImage(game.logo),
      sizedImagesForLogo: mapGameImagesToBeSized(game.logo, table),
    }),
    ...(game?.animation && {
      animation: mapTileAnimationData(game.animation),
    }),
    bannerImage: mapBannerImage(game.game_banner?.image),
    infoPageImages: mapGameInfoImages(game.info_page),
    launchUrl: createLaunchUrl(game, locale, game_launch_placement),
    rules: game.rules,
    sash: game.sash_ref?.[0],
    table: table,
    tableFetching: game.table_id && game.table_id.length > 0 ? true : false,
    //TODO: consider moving path constants to global page properties instead of hardcoding
    gameDetailsPath: createDynamicPath(base_path, getGameDetailsPath(game)),
    gameLaunchPath: createDynamicPath(
      base_path,
      `${getSpecialRoutePath(FunctionalitiesEnum.gameLaunch)}/${
        game.presentation_code
      }`
    ),
    seo: game.seo && {
      title: game.seo.seo_title,
      description: game.seo.seo_description,
    },
    tracking_metadata: {
      title: game.title,
    },
    notIndexed: true,
  };
  return mappedGame;
}
